/**
 * Operations Domain Invariants
 *
 * Enforces:
 * 1. Permissions (`canApply`) — source-based matrix (user / ai / api / url).
 * 2. Normalization — clamp the history page; keep the row-approval map in sync
 *    with the loaded change set (drop stale targetIds).
 * 3. Validation — UI feedback (review surface has no free-form form fields).
 */

import { Invariants } from '@commandDrivenStateEngine/core/invariants';
import { OperationsState, selectors } from './state';
import { OperationsCommand } from './commands';

// =============================================================================
// PERMISSIONS — source matrix
// =============================================================================

/**
 * Permission control — who can execute which commands.
 *
 * Source hierarchy:
 * - `api`: policies sync the run + change set + history and flip loading flags.
 *   Also allowed to clear submitting / submission flags after a backend roundtrip.
 * - `url`: navigation only (history pagination).
 * - `ai`: read-only review interactions (toggle/preview the selection,
 *   paginate) but NEVER the approval or rejection that crosses the egress gate,
 *   and never data sync.
 * - `user`: everything except `api/*` (the operator drives review + approval).
 */
const canApply = ({
  command,
  meta
}: {
  state: OperationsState;
  command: OperationsCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // API source: data sync + loading flags + clearing the submitting flag and
  // the review surface after a backend roundtrip.
  if ( source === 'api' ) {
    return (
      type === 'operations/review/loadRun' ||
      type === 'operations/review/setChangeSet' ||
      type === 'operations/review/clear' ||
      type === 'operations/list/setRuns' ||
      type.startsWith( 'operations/loading/' )
    );
  }

  // URL source: history navigation only.
  if ( source === 'url' ) {
    return (
      type === 'operations/list/setPage' ||
      type === 'operations/list/setPerPage'
    );
  }

  // AI source: read-only review interactions and navigation, but NOT the
  // approval/reject that triggers a server mutation crossing the egress gate,
  // and NOT data sync (api-sourced).
  if ( source === 'ai' ) {
    if ( type === 'operations/review/submitApproval' ) return false;
    if ( type === 'operations/review/reject' ) return false;
    if ( type === 'operations/review/loadRun' ) return false;
    if ( type === 'operations/review/setChangeSet' ) return false;
    if ( type === 'operations/list/setRuns' ) return false;
    if ( type.startsWith( 'operations/loading/' )) return false;
    return true;
  }

  // User source: everything except api/* data-sync commands.
  if ( source === 'user' ) {
    if ( type === 'operations/review/loadRun' ) return false;
    if ( type === 'operations/review/setChangeSet' ) return false;
    if ( type === 'operations/list/setRuns' ) return false;
    if ( type.startsWith( 'operations/loading/' )) return false;
    return true;
  }

  // Unknown sources: deny.
  return false;
};

// =============================================================================
// NORMALIZATION
// =============================================================================

const normalize = ( state: OperationsState ): OperationsState => {
  let normalized = state;

  // Clamp history page within bounds.
  const totalPages = selectors.totalHistoryPages( state );
  if ( normalized.list.currentPage > totalPages ) {
    normalized = {
      ...normalized,
      list: { ...normalized.list, currentPage: totalPages }
    };
  }
  if ( normalized.list.currentPage < 1 ) {
    normalized = {
      ...normalized,
      list: { ...normalized.list, currentPage: 1 }
    };
  }

  // Drop row-approval entries for targets not present in the loaded change set,
  // so a stale selection can never leak into `approvedItemIds`.
  const items = normalized.review.changeSet?.items ?? [];
  if ( items.length > 0 ) {
    const validIds = new Set( items.map(( item ) => item.targetId ));
    const entries = Object.entries( normalized.review.rowApproval );
    const hasStale = entries.some(([id]) => !validIds.has( id ));
    if ( hasStale ) {
      const pruned = entries.reduce<Record<string, boolean>>(( acc, [ id, approved ]) => {
        if ( validIds.has( id )) acc[id] = approved;
        return acc;
      }, {});
      normalized = {
        ...normalized,
        review: { ...normalized.review, rowApproval: pruned }
      };
    }
  } else if ( Object.keys( normalized.review.rowApproval ).length > 0 ) {
    // No change set loaded → no selection.
    normalized = {
      ...normalized,
      review: { ...normalized.review, rowApproval: {}}
    };
  }

  return normalized;
};

// =============================================================================
// VALIDATION
// =============================================================================

const validate = (): Record<string, string> | null => {
  // The review surface has no free-form form fields — approval gating is
  // enforced in the component (submit disabled when `hasApproval` is false).
  return null;
};

export const invariants: Invariants<OperationsState, OperationsCommand> = {
  normalize,
  canApply,
  validate
};
